/**
 * @file 화면 오류 안내 (흰 화면 대신)
 * @description 화면을 그리다 스크립트 오류가 나면 앱 전체가 흰 화면이 된다. 대신 안내 + 새로고침 버튼을 보여준다
 *              - 새 버전 배포 후 새로고침 안 한 탭: 옛 JS 가 없어진 화면 파일(청크)을 받으려다 실패 (ChunkLoadError)
 *                → "새 버전이 있어요" 안내. 버튼 이벤트 등 화면 밖에서 난 청크 오류도 잡는다 (unhandledrejection)
 *              - 그 외 오류 → "일시적인 오류" 안내
 *              - 버튼은 지금 주소 새로고침. 로그인은 새로고침 후 앱 시작(bootstrapSession)이 판단한다
 *                (세션 살아있으면 그대로, 풀렸으면 재로그인 모달, 이 브라우저 로그인 표시가 없으면 Logined 지우고 로그인 페이지)
 *              - 문구 파일(locale)도 청크라서 못 받을수 있다 → react-intl 없이 여기 문구를 쓴다
 */
import { Component, ReactNode } from 'react'
import oc from 'open-color'
import { cookies } from 'utils'

type Kind = 'update' | 'error'

type Props = { children: ReactNode }
type State = { kind: Kind | null }

type Text = Record<Kind, { title: string, desc: string }> & { button: string }

const TEXT: { [lang: string]: Text } = {
    ko: {
        update: { title: '새 버전이 배포되었습니다', desc: '화면을 새로고침하면 최신 버전으로 이어서 사용할 수 있습니다.' },
        error: { title: '일시적인 오류가 발생했습니다', desc: '화면을 새로고침한 뒤 다시 시도해 주세요.' },
        button: '새로고침'
    },
    en: {
        update: { title: 'A new version is available', desc: 'Refresh the page to continue with the latest version.' },
        error: { title: 'Something went wrong', desc: 'Please refresh the page and try again.' },
        button: 'Refresh'
    },
    jp: {
        update: { title: '新しいバージョンが公開されました', desc: '画面を再読み込みすると最新バージョンで引き続き利用できます。' },
        error: { title: '一時的なエラーが発生しました', desc: '画面を再読み込みしてからもう一度お試しください。' },
        button: '再読み込み'
    },
    ru: {
        update: { title: 'Доступна новая версия', desc: 'Обновите страницу, чтобы продолжить работу в новой версии.' },
        error: { title: 'Произошла временная ошибка', desc: 'Обновите страницу и попробуйте снова.' },
        button: 'Обновить'
    },
    vi: {
        update: { title: 'Đã có phiên bản mới', desc: 'Hãy tải lại trang để tiếp tục sử dụng phiên bản mới nhất.' },
        error: { title: 'Đã xảy ra lỗi tạm thời', desc: 'Vui lòng tải lại trang và thử lại.' },
        button: 'Tải lại'
    }
}

export const isChunkLoadError = (e: any) => {
    if (!e) return false
    const message = String(e.message || e)
    return e.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(message) || /dynamically imported module/i.test(message)
}

class AppErrorBoundary extends Component<Props, State> {
    state: State = { kind: null }

    static getDerivedStateFromError(error: any): State {
        return { kind: isChunkLoadError(error) ? 'update' : 'error' }
    }

    componentDidCatch(error: any, info: any) {
        console.error('AppErrorBoundary', error, info)
    }

    componentDidMount() {
        window.addEventListener('unhandledrejection', this.handleRejection)
    }

    componentWillUnmount() {
        window.removeEventListener('unhandledrejection', this.handleRejection)
    }

    handleRejection = (e: PromiseRejectionEvent) => {
        if (isChunkLoadError(e.reason)) this.setState({ kind: 'update' })
    }

    handleReload = () => {
        window.location.reload()
    }

    render() {
        const { kind } = this.state

        if (!kind) return this.props.children

        const text = TEXT[cookies.get('NEXT_LOCALE')] || TEXT.ko

        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 40000000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: oc.gray[0] }}>
                <div style={{ width: '100%', maxWidth: 400, padding: '36px 32px 28px', borderRadius: 16, background: 'white', boxShadow: '0 4px 24px rgba(0, 0, 0, .08)', textAlign: 'center', boxSizing: 'border-box' }}>
                    <div style={{ width: 52, height: 52, margin: '0 auto 16px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: kind === 'update' ? oc.blue[0] : oc.orange[0], color: kind === 'update' ? oc.blue[7] : oc.orange[7], fontSize: 26, fontWeight: 700 }}>
                        {kind === 'update' ? '↻' : '!'}
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: oc.gray[9], wordBreak: 'keep-all' }}>{text[kind].title}</div>
                    <div style={{ marginTop: 10, fontSize: 14, lineHeight: 1.6, color: oc.gray[7], wordBreak: 'keep-all' }}>{text[kind].desc}</div>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        style={{ width: '100%', height: 48, marginTop: 24, border: 0, borderRadius: 8, background: '#3f51b5', color: 'white', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
                    >
                        {text.button}
                    </button>
                </div>
            </div>
        )
    }
}

export default AppErrorBoundary
